/**
 * What the robot drawer and the Fleet table may honestly say about a robot
 * (twin slice 1, plan W3 step 14).
 *
 * Every line here reads the robot's PRESENCE (`robot-presence.ts`), the
 * same classification the map draws from, so the drawer can never call a
 * robot here and now while the map draws its ghost. Before this, both
 * printed the record as it stood: a battery from a roster stub, the
 * coordinates of the (0, 0) lost sentinel, and a "Last seen" the worker
 * re-stamps every second on a robot nobody has heard from for an hour.
 *
 * THE LINES:
 *
 *   battery      the reported figure only while the record is real AND
 *                live (presence `live`, feed not clamped); "—" otherwise
 *   location     the FLOOR of the record's pose while it is drawn there
 *                ("L9"), never its coordinates: they are the robot map's
 *                frame units, which read as metres, and a site audience
 *                never sees them (ops read them in the drawer's Details,
 *                `mapPositionDetail`, labelled as map units); "In lift SL1"
 *                while riding; "<floor> · Position updating after lift."
 *                while settling (`SETTLING_TEXT`, the resolver's words);
 *                "No signal · last seen in SL1 at HH:MM" for a ghost
 *                that stands inside a car (presence's `liftName` on a
 *                lost robot, such as a rider gone silent) when the robot
 *                itself is unheard (`UNHEARD`), "Last recorded in SL1 at
 *                HH:MM" when it may still be reporting (the (0, 0)
 *                sentinel); "Last recorded L9 at HH:MM" for any other
 *                ghost; "Not reporting a position" when there is none
 *   last report  the time of the last REAL record: the record's own stamp
 *                while it is real-shaped, the ghost's for a lost robot,
 *                "—" when neither exists
 *   heard        "heard N s ago" -- this page's own receipt age, on the
 *                monotonic clock -- only where receipts mean the robot:
 *                a stub's stamp advances because the WORKER re-stamps it,
 *                so for a stub or a reason it would claim a robot is
 *                talking when only the worker is. And only from the
 *                SECOND receipt since the last reset (the two-receipt
 *                rule): the first record after a stream opens may be a
 *                replay of an old row, and counting it stamps "heard
 *                0 s ago" on a robot nobody has heard from. Until then
 *                it reads "checking…", as the map's dim mark does
 *
 * Times are on the site's clock, with the day named when it is not today
 * and "(device time)" when the site declares no zone (`formatClock`).
 */
import type { RobotStateView } from '../../api/rmf-client';
import {
  SETTLING_TEXT,
  UNHEARD_CAUSES as UNHEARD,
  type PresenceCause,
  type PresencePose,
  type RobotPresence,
} from '../Map/robot-presence';
import { formatClock } from '../Devices/lift-reading';

/** The record is a real one, reporting now: the only time a figure it
 *  carries (battery, the status pulse) may be shown as current. */
export function isReportingNow(presence: RobotPresence | undefined, clamped: boolean): boolean {
  return !clamped && presence?.kind === 'live';
}

export function batteryText(
  battery: number | undefined,
  presence: RobotPresence | undefined,
  clamped: boolean,
): string {
  if (!isReportingNow(presence, clamped)) return '—';
  if (typeof battery !== 'number' || !Number.isFinite(battery)) return '—';
  return `${battery}%`;
}

/** The robot has lost its place on the map: powered and reporting, but
 *  unplaceable (`pending-localize`, or a localisation reason). */
export function needsLocalising(
  robot: Pick<RobotStateView, 'status'>,
  presence: RobotPresence | undefined,
): boolean {
  return robot.status === 'pending-localize' || presence?.kind === 'unknown';
}

// "No signal" only where the robot itself is unheard: `UNHEARD_CAUSES`
// (robot-presence), one set shared with the attention list's rows.
export function locationText(
  presence: RobotPresence | undefined,
  timeZone: string | null,
  wallNowMs: number,
): string {
  if (!presence) return 'Not reporting a position';
  if (presence.kind === 'riding' && presence.liftName) return `In lift ${presence.liftName}`;
  const pose = presence.pose;
  if (!pose) return 'Not reporting a position';
  if (pose.source !== 'live') {
    const at =
      pose.atMs !== null && Number.isFinite(pose.atMs)
        ? ` at ${formatClock(pose.atMs, timeZone, wallNowMs)}`
        : '';
    // Its last real pose was inside a car: say which, not the floor the
    // pose names (a rider's pose names the floor it boarded on). "No
    // signal" only where the robot really is unheard.
    if (presence.kind === 'lost' && presence.liftName) {
      return UNHEARD.has(presence.cause)
        ? `No signal · last seen in ${presence.liftName}${at}`
        : `Last recorded in ${presence.liftName}${at}`;
    }
    return `Last recorded ${pose.map}${at}`;
  }
  if (presence.kind === 'settling') return `${pose.map} · ${SETTLING_TEXT}`;
  // The floor only. The coordinates are the robot map's frame units and
  // read as metres ("L9 (12.3, -45.6)"); they belong in ops Details.
  return pose.map;
}

/** One row of the robot drawer's ops-only Details. */
export interface PositionDetail {
  readonly label: string;
  readonly value: string;
}

/**
 * Where presence places the robot, in the robot map's own units -- for
 * the drawer's Details, which only the ops audience gets (`audienceFor`),
 * collapsed. Labelled as map units, never metres: the frame is the
 * robot map's, and nothing here says its unit is a metre. Never presented
 * as more current than the lines above it say (`positionLabel`). Null
 * when presence draws nothing.
 */
export function mapPositionDetail(presence: RobotPresence | undefined): PositionDetail | null {
  const pose = presence?.pose;
  if (!presence || !pose || !Number.isFinite(pose.x) || !Number.isFinite(pose.y)) return null;
  return {
    label: positionLabel(presence.kind, pose),
    value: `${pose.map} · x ${pose.x.toFixed(1)}, y ${pose.y.toFixed(1)} (map units, not metres)`,
  };
}

/**
 * The Details row's label, by presence KIND as well as the pose's source:
 * a settling robot (just off a ride) and a checking one (seen once, which
 * may be a replay of an old row, rule 6) are drawn at their own record,
 * `source: 'live'`, but the Location and Last report lines above say the
 * position is updating or not yet vouched for -- so the label says so too.
 * A ghost's pose says "Last recorded", as its location line does.
 */
function positionLabel(kind: RobotPresence['kind'], pose: PresencePose): string {
  if (pose.source !== 'live') return 'Last recorded position';
  if (kind === 'settling') return 'Position (updating after lift)';
  if (kind === 'checking') return 'Position (not yet confirmed)';
  return 'Position';
}

/** Causes whose record is the robot's own: real-shaped, judged by age. */
const OWN_RECORD: ReadonlySet<PresenceCause> = new Set<PresenceCause>([
  'age',
  'first-receipt',
  'after-ride',
  'riding',
]);

/** The epoch ms of the last REAL record (worker clock), or null. */
export function lastReportAtMs(
  robot: Pick<RobotStateView, 'updatedAtMs'>,
  presence: RobotPresence | undefined,
): number | null {
  if (!presence) return null;
  // A lost robot's last real record is its ghost's, whatever the current
  // record (a stub, a re-stamp) says.
  if (presence.kind === 'lost') {
    const ghost = presence.pose && presence.pose.source !== 'live' ? presence.pose : null;
    return ghost?.atMs != null && Number.isFinite(ghost.atMs) ? ghost.atMs : null;
  }
  if (!OWN_RECORD.has(presence.cause)) return null;
  const at = robot.updatedAtMs;
  return typeof at === 'number' && Number.isFinite(at) ? at : null;
}

export function lastReportText(
  robot: Pick<RobotStateView, 'updatedAtMs'>,
  presence: RobotPresence | undefined,
  timeZone: string | null,
  wallNowMs: number,
): string {
  const at = lastReportAtMs(robot, presence);
  return at === null ? '—' : formatClock(at, timeZone, wallNowMs);
}

/** What this page's receipts say about one robot (`RobotReceipts`). */
export interface HeardFacts {
  /** Monotonic ms since the last counted receipt (`now() - lastAt`);
   *  null when this page has never heard the robot. */
  readonly agoMs: number | null;
  /** Strictly newer records counted since the last reset. */
  readonly count: number;
}

/**
 * The receipt age stands for the robot only from the second counted
 * receipt since the last reset -- the same two receipts presence needs
 * to leave "checking" (`classifyPresence`, rule 6). A reset forgets the
 * last stamp, so the first record after a stream reopens counts however
 * old it is, and it moves `lastAt` to NOW: a replayed row would read as
 * "heard 0 s ago". With no receipt since the reset, `lastAt` may itself
 * be such a first record, so it is not vouched for either.
 */
function vouched(heard: HeardFacts): boolean {
  return heard.count >= 2;
}

/**
 * "heard N s ago" from this page's receipt age, or null where receipts do
 * not mean the robot (a stub, a reason, the sentinel, `offline`) --
 * "not heard since this page opened" when this page has had none, and
 * "checking…" while the age is not yet vouched for (`vouched`). A lost
 * robot whose age is not vouched for says nothing more: its "Last
 * report" already names the time of its last real record.
 */
export function heardText(presence: RobotPresence | undefined, heard: HeardFacts): string | null {
  if (!presence || !OWN_RECORD.has(presence.cause)) return null;
  const ago = heard.agoMs;
  if (ago === null || !Number.isFinite(ago) || ago < 0) {
    return 'not heard since this page opened';
  }
  if (!vouched(heard)) return presence.kind === 'lost' ? null : 'checking…';
  const s = Math.floor(ago / 1000);
  if (s < 60) return `heard ${s} s ago`;
  const min = Math.floor(s / 60);
  if (min < 60) return `heard ${min} min ago`;
  return 'heard over an hour ago';
}

/** How often "heard N s ago" must re-render to stay true: every second
 *  only while it is shown, and in seconds. */
export function heardTickMs(heard: HeardFacts): number {
  return vouched(heard) && heard.agoMs !== null && heard.agoMs < 60_000 ? 1_000 : 60_000;
}
